import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { IconButton } from './Button/Button';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu as ReactstrapDropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { FaArrowCircleRight, FaLock, FaSave } from 'react-icons/all';
import { ChevronDown } from 'lucide-react';
import HelpPopover from './HelpPopover';
import { Tabs, TabsList, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { useHistory, useLocation } from 'react-router-dom';
import i18n from '@/utils/i18n';
import { Button } from './ui/button';
import { BottomBar } from './BottomBar/BottomBar';
import { Spinner } from './ui/spinner';

type LayoutProps = {
  children: React.ReactNode;
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <div // when changing this, make sure that the sticky table header in /edit-container-prices still works!
      ref={scrollContainerRef}
      style={{
        minHeight: '100%',
        overflow: 'auto',
        width: '100%',
        backgroundColor: 'var(--bs-body-bg)',
      }}
    >
      {/* when changing this, make sure that the sticky table header in /edit-container-prices still works! */}
      <div className="main-content-container position-relative">{children}</div>
    </div>
  );
};

type PageContainerProps = {
  children: any;
};

export const PageContainer: React.FC<PageContainerProps> = ({ children }) => {
  return <div style={{ marginBottom: '50vh' }}>{children}</div>;
};

type PageHeaderProps = {
  title: string;
  subtitle?: string;
  secondSubtitle?: string;
  children?: any;
  qrCode?: any;
  selfReturnQrCode?: any;
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  secondSubtitle,
  children,
  qrCode,
  selfReturnQrCode,
}) => {
  return (
    <div className="d-flex flex-column flex-sm-row my-sm-3 my-md-4 my-lg-5 my-3">
      <div className="flex-fill">
        <h1 className="mb-2">{title}</h1>
        {subtitle && <h4>{subtitle}</h4>}
        {secondSubtitle && <h4>{secondSubtitle}</h4>}
        {children}
      </div>
      {qrCode && (
        <div className="ms-sm-4 mt-2 flex-shrink-0">
          {qrCode}
          {selfReturnQrCode && (
            <p style={{ textAlign: 'center' }}>User QR code</p>
          )}
        </div>
      )}
      {selfReturnQrCode && (
        <div className="ms-sm-4 mt-2 flex-shrink-0">
          {selfReturnQrCode}
          <p style={{ textAlign: 'center' }}>Self checkin QR code</p>
        </div>
      )}
    </div>
  );
};

type PageHeaderButtonProps = {
  icon?: object;
  label: string;
  onClick: () => void;
  isLoading?: boolean;
};

export const PageHeaderButton: React.FC<PageHeaderButtonProps> = ({
  icon,
  label,
  onClick,
  isLoading = false,
}) => {
  return (
    <IconButton
      className="mb-1 me-1"
      icon={icon}
      label={label}
      onClick={onClick}
      isLoading={isLoading}
    />
  );
};

type SearchBarProps = {
  label: string;
  placeholder: string;
  onKeyUp: (e: any) => void;
  name: string;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  label,
  placeholder,
  onKeyUp,
  name,
}) => {
  return (
    <div className="input-group mb-2">
      <div className="input-group-text">{label}</div>
      <input
        type="text"
        name={name}
        className="form-control"
        autoComplete="off"
        placeholder={placeholder}
        onKeyUp={onKeyUp}
      />
    </div>
  );
};

type TabsComponentProps = {
  tabs: {
    title: string;
    content?: any;
    icon?: any;
    hide?: boolean;
    onClick?: any;
    children?: Array<{
      title: string;
      content: any;
      icon?: any;
      hide?: boolean;
      onClick?: () => void;
    }>;
  }[];
  activeTab?: string | number;
  activeSubTab?: string | number;
  isSubTab?: boolean; // true, if this is a TabComponent within another tab Component
  className?: string;
  // Query params to carry over when switching tabs. Tab clicks rebuild the
  // search string, so page-level URL state (e.g. the partner search keyword)
  // is lost unless listed here.
  preserveParams?: string[];
};

function buildTabSwitchQuery(options: {
  currentSearch: string;
  index: number | string;
  isSubTab?: boolean;
  activeTab?: string | number;
  preserveParams?: string[];
}) {
  const { currentSearch, index, isSubTab, activeTab, preserveParams } = options;
  const params = new URLSearchParams();
  if (preserveParams?.length) {
    const current = new URLSearchParams(currentSearch);
    for (const key of preserveParams) {
      const value = current.get(key);
      if (value !== null) params.set(key, value);
    }
  }
  params.set('activeTab', String(isSubTab ? activeTab : index));
  if (isSubTab) {
    params.set('activeSubTab', String(index));
  }
  return `?${params.toString()}`;
}

// Bootstrap version - kept for backward compatibility
export const TabsComponentBootstrap: React.FC<TabsComponentProps> = ({
  tabs,
  activeTab,
  className,
  activeSubTab,
  isSubTab,
  preserveParams,
}) => {
  const history = useHistory();
  const location = useLocation();

  const tabIndexToCheck = isSubTab ? activeSubTab : activeTab;

  // Dropdown
  const [dropdownOpen, setDropdownOpen] = useState<string | undefined>(
    undefined,
  );

  // Handlers
  const toggleDropdown = (title: string) => {
    if (dropdownOpen === title) {
      setDropdownOpen(undefined);
    } else {
      setDropdownOpen(title);
    }
  };

  const handleClick = (tab: any, index: number | string) => {
    history.push({
      search: buildTabSwitchQuery({
        currentSearch: location.search,
        index,
        isSubTab,
        activeTab,
        preserveParams,
      }),
    });
    if (tab.onClick) {
      tab.onClick();
    }
  };

  return (
    <div className={className}>
      <div data-use-bs>
        <Nav pills>
          {tabs
            .filter((tab) => !tab.hide)
            .map((tab, index) => {
              return !tab.children ? (
                <NavItem key={tab.title.replace(/\s/g, '')}>
                  <NavLink
                    onClick={() => handleClick(tab, index)}
                    active={tabIndexToCheck === index}
                    href="#"
                  >
                    {tab.icon && (
                      <span
                        className="me-2"
                        style={{ position: 'relative', top: '-1px' }}
                      >
                        {tab.icon}
                      </span>
                    )}
                    {tab.title}
                  </NavLink>
                </NavItem>
              ) : (
                <Dropdown
                  nav
                  isOpen={dropdownOpen === tab.title}
                  toggle={() => toggleDropdown(tab.title)}
                >
                  <DropdownToggle nav caret>
                    {tab.icon && (
                      <span
                        className="me-2"
                        style={{ position: 'relative', top: '-1px' }}
                      >
                        {tab.icon}
                      </span>
                    )}

                    {tab.title}
                  </DropdownToggle>
                  <ReactstrapDropdownMenu>
                    {tab.children?.map((childTab, childIndex) => {
                      return (
                        <DropdownItem
                          key={'child' + childTab.title.replace(/\s/g, '')}
                        >
                          <NavLink
                            onClick={() =>
                              handleClick(childTab, `${index}_${childIndex}`)
                            }
                            active={tabIndexToCheck === index}
                            href="#"
                          >
                            {childTab.icon && (
                              <span
                                className="me-2"
                                style={{ position: 'relative', top: '-1px' }}
                              >
                                {childTab.icon}
                              </span>
                            )}
                            {childTab.title}
                          </NavLink>
                        </DropdownItem>
                      );
                    })}
                  </ReactstrapDropdownMenu>
                </Dropdown>
              );
            })}
        </Nav>
      </div>

      <div className="mt-5" style={{ minHeight: '70vh' }}>
        {tabs.map((tab, key) => {
          if (String(tabIndexToCheck) === String(key) && !tab.hide) {
            return <div key="content">{tab.content}</div>;
          }

          // Children
          if (tab.children) {
            return tab.children.map((childTab, childKey) => {
              if (
                String(tabIndexToCheck) === `${key}_${childKey}` &&
                !childTab.hide
              ) {
                return <div key="content">{childTab.content}</div>;
              }

              return null;
            });
          }

          return null;
        })}
      </div>
    </div>
  );
};

// Tailwind version - uses shadcn primitives
export const TabsComponent: React.FC<TabsComponentProps> = ({
  tabs,
  activeTab,
  className,
  activeSubTab,
  isSubTab,
  preserveParams,
}) => {
  const history = useHistory();
  const location = useLocation();
  const tabIndexToCheck = isSubTab ? activeSubTab : activeTab;
  const visibleTabs = tabs.filter((tab) => !tab.hide);

  const handleTabChange = (
    tab: TabsComponentProps['tabs'][0],
    index: number | string,
  ) => {
    history.push({
      search: buildTabSwitchQuery({
        currentSearch: location.search,
        index,
        isSubTab,
        activeTab,
        preserveParams,
      }),
    });
    tab.onClick?.();
  };

  const activeContent = React.useMemo(() => {
    for (let key = 0; key < tabs.length; key++) {
      const tab = tabs[key];
      if (String(tabIndexToCheck) === String(key) && !tab.hide) {
        return tab.content;
      }
      if (tab.children) {
        for (let childKey = 0; childKey < tab.children.length; childKey++) {
          const childTab = tab.children[childKey];
          if (
            String(tabIndexToCheck) === `${key}_${childKey}` &&
            !childTab.hide
          ) {
            return childTab.content;
          }
        }
      }
    }
    return null;
  }, [tabs, tabIndexToCheck]);

  return (
    <Tabs
      value={String(tabIndexToCheck ?? 0)}
      className={cn('tw:w-full', className)}
    >
      {/* data-no-bs: the triggers are shadcn buttons — opt out of the scoped
          Bootstrap styles when the page is under a [data-use-bs] route
          (e.g. /partner/merchants), like MerchantTransactionsTab does. */}
      <TabsList
        data-no-bs
        className="tw:flex-wrap tw:h-auto tw:gap-1 tw:mb-8 tw:bg-transparent"
      >
        {visibleTabs.map((tab) => {
          const originalIndex = tabs.indexOf(tab);
          const isActive =
            String(tabIndexToCheck) === String(originalIndex) ||
            tab.children?.some(
              (_, ci) => String(tabIndexToCheck) === `${originalIndex}_${ci}`,
            );

          return !tab.children ? (
            // Same look as the dropdown tabs below (background + border radius),
            // just without a menu.
            <Button
              key={tab.title.replace(/\s/g, '')}
              variant={isActive ? 'default' : 'secondary'}
              onClick={() => handleTabChange(tab, originalIndex)}
            >
              {tab.icon && <span>{tab.icon}</span>}
              {tab.title}
            </Button>
          ) : (
            <DropdownMenu key={tab.title.replace(/\s/g, '')}>
              <DropdownMenuTrigger asChild>
                <Button variant={isActive ? 'default' : 'secondary'}>
                  {tab.icon && <span>{tab.icon}</span>}
                  {tab.title}
                  <ChevronDown className="tw:size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" collisionPadding={16}>
                {tab.children.map((childTab, childIndex) => {
                  const isChildActive =
                    String(tabIndexToCheck) ===
                    `${originalIndex}_${childIndex}`;
                  return (
                    <DropdownMenuItem
                      key={`child-${childTab.title.replace(/\s/g, '')}`}
                      onClick={() =>
                        handleTabChange(
                          childTab,
                          `${originalIndex}_${childIndex}`,
                        )
                      }
                      className={cn(
                        isChildActive && 'tw:bg-accent tw:font-medium',
                      )}
                    >
                      {childTab.icon && (
                        <span className="tw:mr-1.5">{childTab.icon}</span>
                      )}
                      {childTab.title}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </TabsList>

      <TabsContent
        value={String(tabIndexToCheck ?? 0)}
        className="tw:mt-5 tw:min-h-[70vh]"
      >
        {activeContent}
      </TabsContent>
    </Tabs>
  );
};

type EditSectionProps = {
  title?: string;
  children: any;
  onSave?: (() => void) | (() => Promise<void>) | null;
  isLoading?: boolean;
  className?: string;
  saveButtonText?: string;
};

export const EditSection: React.FC<EditSectionProps> = ({
  title = '',
  children,
  onSave,
  isLoading,
  className,
  saveButtonText,
}) => {
  return (
    <div data-use-bs className={className}>
      <div className="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center mb-3">
        <h3>{title}</h3>
        {onSave && (
          <BottomBar className="//does-not-use-bs">
            <div className="tw:w-full tw:flex tw:p-4 tw:py-3.5 tw:justify-end tw:bg-sidebar tw:border-t tw:border-sidebar-border">
              <Button disabled={isLoading} onClick={onSave}>
                {isLoading && <Spinner />}
                {saveButtonText != null
                  ? saveButtonText
                  : i18n.t('Common:saveSection', { section: title })}
              </Button>
            </div>
          </BottomBar>
        )}
      </div>
      {children}
    </div>
  );
};

type EditItemProps = {
  title: string;
  children: any;
  helpText?: React.ReactNode;
  lockIcon?: boolean;
};

export const EditItem: React.FC<EditItemProps> = ({
  title,
  children,
  helpText,
  lockIcon,
}) => {
  return (
    <div className="mb-5">
      <h4 className="mb-2">
        {lockIcon && (
          <FaLock
            className="me-2"
            style={{ position: 'relative', top: '-0.16rem' }}
          />
        )}
        {!lockIcon && (
          <FaArrowCircleRight
            className="me-2"
            style={{ position: 'relative', top: '-0.16rem' }}
          />
        )}
        {title}
        {helpText && (
          <HelpPopover
            size="sm"
            text={helpText}
            id={`${title.replace(/\s/g, '')}-help`}
          />
        )}
      </h4>
      {children}
    </div>
  );
};

type LoadingContainerProps = {
  hasErrorLoading?: boolean;
  errorMessage?: string;
  className?: string;
};

export const LoadingContainer: React.FC<LoadingContainerProps> = ({
  hasErrorLoading,
  errorMessage,
  className,
}) => {
  return (
    <div className={className} data-use-bs>
      {hasErrorLoading ? (
        <p>
          <em>{errorMessage}</em>
        </p>
      ) : (
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}
    </div>
  );
};

type ResponsiveTableProps = {
  children?: any;
  className?: string;
  fixed?: boolean;
};

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  children,
  className,
  fixed = false,
}) => {
  const styleOptions: any = fixed ? { tableLayout: 'fixed' } : {};
  return (
    <div className={className} style={{ overflowX: 'scroll' }}>
      <table
        className="table-striped table-borderless table-hover table bg-white"
        style={{ ...styleOptions }}
      >
        {children}
      </table>
    </div>
  );
};
